// The canonical record-set model (spec §2):
//   { records: [{ key, fields: { path: value } }], schema: { path: type } }
//
// One engine, many parsers: a nested object becomes a SINGLE record whose fields
// are its flattened leaf paths; an array of objects becomes one record per
// element (keyed by a chosen field). CSV (Phase 7) plugs into the same shape.
import { flatten, typeOf } from './flatten.js';

const ROOT = '$root';

export function toRecordSet(parsed, { keyColumns = [] } = {}) {
  let records;
  if (Array.isArray(parsed)) {
    records = parsed.map((el, i) => ({ key: deriveKey(el, keyColumns, i), fields: flatten(el), index: i }));
  } else {
    // js-yaml represents an empty/comment-only document as undefined. It has no
    // fields; flattening it as `$: undefined` fabricates a semantic value.
    records = [{ key: ROOT, fields: parsed === undefined ? Object.create(null) : flatten(parsed), index: 0 }];
  }

  // Object.create(null): a field path of `__proto__` must be an own key here too.
  const schema = Object.create(null);
  for (const rec of records) {
    for (const [path, value] of Object.entries(rec.fields)) {
      if (!(path in schema)) schema[path] = typeOf(value);
    }
  }
  return { records, schema };
}

export const ROOT_KEY = ROOT;

function deriveKey(el, keyColumns, index) {
  if (keyColumns.length && el && typeof el === 'object') {
    // Object.hasOwn: a key column literally named `toString`/`constructor` must
    // read the element's OWN field — a bare el[c] would otherwise resolve to the
    // inherited prototype function and key every record identically (REL-055).
    const key = keyColumns
      .map((c) => (Object.hasOwn(el, c) ? el[c] : undefined))
      .filter((v) => v !== undefined)
      .join('|');
    if (key) return key;
  }
  return `#${index}`;
}
